import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation } from "react-router";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Upload, FolderOpen, Loader2, AlertCircle, Package, Database, XCircle, HardDrive } from "lucide-react";
import { api, type Folder, type Project, type Bucket } from "~/services/api";
import { cn } from "~/lib/utils";
import { useStorageStatus } from "../hooks/useStorageStatus";

interface UploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  uploadedBytes: number;
  abortController?: AbortController;
}

export function GlobalUpload() {
  const [open, setOpen] = useState(false);
  const params = useParams();
  const location = useLocation();
  const { data: storageStatus } = useStorageStatus();
  const uploadsBlocked = storageStatus?.is_critical ?? false;

  // Close GlobalUpload if on bucket detail page to prevent overlay stacking with UploadSheet
  useEffect(() => {
    const isBucketDetailPage = location.pathname.includes('/buckets/') &&
                                params.bucketId &&
                                params.projectId;
    if (open && isBucketDetailPage) {
      setOpen(false);
    }
  }, [location.pathname, params.bucketId, params.projectId, open]);

  // Selection state
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [selectedBucketId, setSelectedBucketId] = useState<string>("");
  const [selectedFolderId, setSelectedFolderId] = useState<string>("root");

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [files, setFiles] = useState<UploadFile[]>([]);

  // Loading state
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingBuckets, setIsLoadingBuckets] = useState(false);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drag state
  const [isDragOver, setIsDragOver] = useState(false);

  // Upload queue - use refs not state!
  const uploadQueueRef = useRef<string[]>([]);
  const activeUploadsRef = useRef<Set<string>>(new Set());
  const maxConcurrent = 3;

  // Global keyboard shortcut listener
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Only trigger if not typing in an input/textarea
      const target = e.target as HTMLElement;
      const isInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      // Don't open GlobalUpload on bucket detail pages (use local UploadSheet instead)
      const isBucketDetailPage = location.pathname.includes('/buckets/') &&
                                  params.bucketId &&
                                  params.projectId;

      if (e.key === "u" && !isInput && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey && !isBucketDetailPage) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [location.pathname, params.bucketId, params.projectId]);

  // Smart context detection - pre-fill project/bucket if in files view
  useEffect(() => {
    if (!open) return;

    const projectId = params.projectId;
    const bucketId = params.bucketId;

    if (projectId && bucketId) {
      // We're in a bucket view - pre-select project and bucket
      setSelectedProjectId(projectId);
      setSelectedBucketId(bucketId);
    }
  }, [open, params.projectId, params.bucketId]);

  // Load projects when opening
  useEffect(() => {
    if (!open) return;

    const loadProjects = async () => {
      setIsLoadingProjects(true);
      setError(null);
      try {
        const projectsList = await api.getProjects();
        setProjects(projectsList);
      } catch (err) {
        console.error("Failed to load projects:", err);
        setError("Failed to load projects");
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadProjects();
  }, [open]);

  // Load buckets when project is selected
  useEffect(() => {
    if (!selectedProjectId) {
      setBuckets([]);
      return;
    }

    const loadBuckets = async () => {
      setIsLoadingBuckets(true);
      setError(null);
      try {
        const bucketsList = await api.getBucketsForProject(parseInt(selectedProjectId));
        setBuckets(bucketsList);
      } catch (err) {
        console.error("Failed to load buckets:", err);
        setError("Failed to load buckets");
      } finally {
        setIsLoadingBuckets(false);
      }
    };

    loadBuckets();
  }, [selectedProjectId]);

  // Load folders when bucket is selected
  useEffect(() => {
    if (!selectedBucketId) {
      setFolders([]);
      return;
    }

    const loadFolders = async () => {
      setIsLoadingFolders(true);
      setError(null);
      try {
        const folderList = await api.getFolders(parseInt(selectedBucketId), null);
        setFolders(folderList);
      } catch (err) {
        console.error("Failed to load folders:", err);
        setError("Failed to load folders");
      } finally {
        setIsLoadingFolders(false);
      }
    };

    loadFolders();
  }, [selectedBucketId]);

  // Process upload queue
  useEffect(() => {
    const processQueue = () => {
      while (
        activeUploadsRef.current.size < maxConcurrent &&
        uploadQueueRef.current.length > 0
      ) {
        const fileId = uploadQueueRef.current.shift();
        if (fileId) {
          const file = files.find((f) => f.id === fileId);
          if (file && file.status === 'pending') {
            startUpload(fileId);
          }
        }
      }
    };

    processQueue();
  }, [files]);

  // Start upload
  const startUpload = useCallback(
    async (fileId: string) => {
      const uploadFile = files.find((f) => f.id === fileId);
      if (!uploadFile || !selectedBucketId) return;

      activeUploadsRef.current.add(fileId);
      const abortController = new AbortController();

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, status: 'uploading' as const, abortController }
            : f
        )
      );

      try {
        await api.uploadFile(uploadFile.file, parseInt(selectedBucketId), {
          folderId: selectedFolderId === 'root' ? null : Number(selectedFolderId),
          onProgress: (progress, uploadedBytes) => {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === fileId
                  ? { ...f, progress, uploadedBytes }
                  : f
              )
            );
          },
          abortSignal: abortController.signal,
        });

        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? { ...f, status: 'success' as const, progress: 100 }
              : f
          )
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed';
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileId
              ? { ...f, status: 'error' as const, error: errorMessage }
              : f
          )
        );
      } finally {
        activeUploadsRef.current.delete(fileId);
      }
    },
    [files, selectedBucketId, selectedFolderId]
  );

  // Handle files
  const handleFiles = useCallback((fileList: FileList | File[]) => {
    const newFiles: UploadFile[] = Array.from(fileList).map((file) => ({
      id: crypto.randomUUID(),
      file,
      status: 'pending',
      progress: 0,
      uploadedBytes: 0,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
    uploadQueueRef.current.push(...newFiles.map((f) => f.id));
  }, []);

  // Drag handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (e.dataTransfer.files.length > 0) {
        handleFiles(e.dataTransfer.files);
      }
    },
    [handleFiles]
  );

  // Remove file
  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file?.abortController) {
        file.abortController.abort();
      }
      return prev.filter((f) => f.id !== fileId);
    });
    uploadQueueRef.current = uploadQueueRef.current.filter((id) => id !== fileId);
    activeUploadsRef.current.delete(fileId);
  }, []);

  // Reset state when closing
  useEffect(() => {
    if (!open) {
      // Reset all state
      setTimeout(() => {
        setSelectedProjectId("");
        setSelectedBucketId("");
        setSelectedFolderId("root");
        setFiles([]);
        setProjects([]);
        setBuckets([]);
        setFolders([]);
        setError(null);
      }, 300); // Wait for sheet to close
    }
  }, [open]);

  // Get selected project/bucket names
  const selectedProject = projects.find((p) => p.id === parseInt(selectedProjectId));
  const selectedBucket = buckets.find((b) => b.id === parseInt(selectedBucketId));

  // Check if ready to upload
  const canUpload = selectedProjectId && selectedBucketId;

  // Calculate stats
  const stats = {
    total: files.length,
    uploading: files.filter((f) => f.status === 'uploading').length,
    success: files.filter((f) => f.status === 'success').length,
    error: files.filter((f) => f.status === 'error').length,
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <SheetTitle className="text-xl font-semibold">Quick Upload</SheetTitle>
          <SheetDescription className="mt-1">
            Press <kbd className="px-1.5 py-0.5 text-xs font-semibold bg-muted rounded">u</kbd> anywhere to upload
          </SheetDescription>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Project Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Project
            </label>
            <Select
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
              disabled={isLoadingProjects}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={isLoadingProjects ? "Loading projects..." : "Select project"} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={String(project.id)}>
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-purple-500" />
                      <span>{project.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bucket Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Bucket
            </label>
            <Select
              value={selectedBucketId}
              onValueChange={setSelectedBucketId}
              disabled={!selectedProjectId || isLoadingBuckets}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    !selectedProjectId
                      ? "Select a project first"
                      : isLoadingBuckets
                      ? "Loading buckets..."
                      : "Select bucket"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {buckets.map((bucket) => (
                  <SelectItem key={bucket.id} value={String(bucket.id)}>
                    <div className="flex items-center gap-2">
                      <Database className="h-4 w-4 text-blue-500" />
                      <span>{bucket.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Folder Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Folder
            </label>
            <Select
              value={selectedFolderId}
              onValueChange={setSelectedFolderId}
              disabled={!selectedBucketId || isLoadingFolders}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    !selectedBucketId
                      ? "Select a bucket first"
                      : isLoadingFolders
                      ? "Loading folders..."
                      : "Select folder"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="root">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span>Root Folder</span>
                  </div>
                </SelectItem>
                {folders.map((folder) => (
                  <SelectItem key={folder.id} value={String(folder.id)}>
                    <div className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4 text-blue-500" />
                      <span>{folder.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Drop Zone */}
          {canUpload && !uploadsBlocked && (
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer',
                isDragOver
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = true;
                input.onchange = (e) => {
                  const target = e.target as HTMLInputElement;
                  if (target.files && target.files.length > 0) {
                    handleFiles(target.files);
                  }
                };
                input.click();
              }}
            >
              <div className="flex flex-col items-center gap-3">
                <div
                  className={cn(
                    'w-14 h-14 rounded-full flex items-center justify-center',
                    isDragOver ? 'bg-primary/10' : 'bg-muted'
                  )}
                >
                  <Upload
                    className={cn(
                      'w-7 h-7',
                      isDragOver ? 'text-primary' : 'text-muted-foreground'
                    )}
                  />
                </div>
                <div>
                  <p className="font-medium text-base">
                    {isDragOver ? 'Drop files here' : 'Choose files or drag and drop'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    Upload to {selectedProject?.name} / {selectedBucket?.name}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Storage Full - Uploads Blocked */}
          {canUpload && uploadsBlocked && (
            <div className="border-2 border-dashed border-red-300 rounded-lg p-12 text-center bg-red-50">
              <div className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full flex items-center justify-center bg-red-100">
                  <XCircle className="w-7 h-7 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-base text-red-800">
                    Storage Full - Uploads Blocked
                  </p>
                  <p className="text-sm text-red-600 mt-1.5">
                    {storageStatus?.used_bytes_human} / {storageStatus?.limit_bytes_human} used ({Math.round(storageStatus?.used_percentage || 0)}%)
                  </p>
                  <p className="text-sm text-red-600 mt-3">
                    Delete files from the Trash to free up space.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Upload progress and files list would go here */}
          {files.length > 0 && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                {stats.success} of {stats.total} uploaded
                {stats.uploading > 0 && ` • ${stats.uploading} uploading`}
                {stats.error > 0 && ` • ${stats.error} failed`}
              </div>
              <div className="space-y-2">
                {files.map((file) => (
                  <div key={file.id} className="border rounded-lg p-3 bg-card">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{file.file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(file.file.size)}
                          {file.status === 'uploading' && ` • ${file.progress}%`}
                          {file.status === 'success' && ' • Complete'}
                          {file.status === 'error' && ` • ${file.error}`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(file.id)}
                        disabled={file.status === 'uploading'}
                        className="h-8 w-8 p-0"
                      >
                        ×
                      </Button>
                    </div>
                    {file.status === 'uploading' && (
                      <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary transition-all duration-300"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t px-6 py-3 text-xs text-muted-foreground bg-muted/30">
          <div className="flex items-center justify-between">
            <span>
              Press <kbd className="px-1.5 py-0.5 font-semibold bg-background rounded border">u</kbd> to close
            </span>
            {stats.uploading > 0 && (
              <span className="flex items-center gap-1.5">
                <Loader2 className="size-3 animate-spin" />
                {stats.uploading} uploading...
              </span>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
